import React from 'react'
import { useState, useEffect, useRef, useReducer, useContext } from 'react'
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom'
import {
    Button,
    Container,
    FormControl,
    TextField,
    FormControlLabel,
    h4,
    Radio,
    RadioGroup,
    Slide,
    Skeleton,
    Box,
    Alert,
    Chip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material'
import '../App.css'
import Modal from '@mui/material/Modal'
import { DataGrid } from '@mui/x-data-grid'
import { useParams } from 'react-router-dom'
import { MultipleFileUploader } from '../components/MultipleFileUploader'
import PDFWebViewComponent from '../components/PDFWebViewComponent'
import {
    uploadMultipleFilesUpdated,
    generateOutline,
    generateBoth,
    createINDModule2Section,
    extractAndStoreMultiple,
    createOutputVectorstore,
    combinedCreateNewDocument,
    getTemplateTypes,
    getOutlineFromTemplates,
    updateDocumentName,
    deleteDocument,
    duplicateDocument,
    updateRoles,
    inviteUserToOrganization
} from '../utils/utils'
import {
    getOneDocumentSet,
    listAllFiles,
    createMod26PKTable,
} from '../utils/utils'
import plus from '../icons/Plus.svg'
import paperclip from '../icons/Paper clip.svg'
import { useAuthInfo } from '@propelauth/react'
import { useDemo } from '../contexts/DemoContext'
import CustomTable from '../components/CustomTable'
import { CustomTableSkeleton } from '../components/CustomTable'
import OptionsMenu from '../components/OptionsMenu'
import { useUser } from '../contexts/UserContext'
import { formatRelativeDate, getDateFromObjectId, formatCreatedDate } from '../utils/dateUtils'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import IconButton from '@mui/material/IconButton'
import RefreshIcon from '@mui/icons-material/Refresh'
import PeopleIcon from '@mui/icons-material/People'

import RenameFileModal from './TemplatesPages/modals/RenameFileModal'
import GeneratingTemplateModal from './TemplatesPages/steps/modals/GeneratingTemplateModal'

import UsersAndPermissionsModal from './SettingsPage/sections/UsersAndPermissionsSections/modals/UsersAndPermissionsModal'


// Cache configuration
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'artos_document_sets_cache_'

// Cache helper functions
const cacheHelpers = {
    getCacheKey: (orgId, workspaceId, type) => {
        return `${CACHE_KEY_PREFIX}${orgId}_${workspaceId}_${type}`
    },
    
    getFromCache: (orgId, workspaceId, type) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId, workspaceId, type)
            const cached = localStorage.getItem(cacheKey)
            
            if (!cached) return null
            
            const parsedCache = JSON.parse(cached)
            const now = Date.now()
            
            // Check if cache is expired
            if (now - parsedCache.timestamp > CACHE_TTL) {
                localStorage.removeItem(cacheKey)
                return null
            }
            
            return parsedCache.data
        } catch (error) {
            console.error('Error reading from cache:', error)
            return null
        }
    },
    
    saveToCache: (orgId, workspaceId, type, data) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId, workspaceId, type)
            const cacheData = {
                data,
                timestamp: Date.now()
            }
            
            try {
                localStorage.setItem(cacheKey, JSON.stringify(cacheData))
            } catch (error) {
                if (error.name === 'QuotaExceededError') {
                    console.warn('localStorage quota exceeded, clearing old entries...')
                    cacheHelpers.clearOldCacheEntries()
                    
                    // Try again after clearing old entries
                    try {
                        localStorage.setItem(cacheKey, JSON.stringify(cacheData))
                    } catch (retryError) {
                        console.error('Failed to save to cache even after clearing old entries')
                    }
                }
            }
        } catch (error) {
            console.error('Error saving to cache:', error)
        }
    },
    
    clearCache: (orgId, workspaceId, type) => {
        try {
            if (type && workspaceId) {
                // Clear specific cache for workspace and type
                const cacheKey = cacheHelpers.getCacheKey(orgId, workspaceId, type)
                localStorage.removeItem(cacheKey)
            } else if (workspaceId) {
                // Clear all caches for specific workspace
                const keysToRemove = []
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i)
                    if (key && key.includes(`${CACHE_KEY_PREFIX}${orgId}_${workspaceId}_`)) {
                        keysToRemove.push(key)
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key))
            } else {
                // Clear all caches for this org
                const keysToRemove = []
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i)
                    if (key && key.startsWith(`${CACHE_KEY_PREFIX}${orgId}_`)) {
                        keysToRemove.push(key)
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key))
            }
        } catch (error) {
            console.error('Error clearing cache:', error)
        }
    },
    
    clearOldCacheEntries: () => {
        const now = Date.now()
        const keysToRemove = []
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                try {
                    const cached = JSON.parse(localStorage.getItem(key))
                    if (now - cached.timestamp > CACHE_TTL) {
                        keysToRemove.push(key)
                    }
                } catch (error) {
                    keysToRemove.push(key)
                }
            }
        }
        
        keysToRemove.forEach(key => localStorage.removeItem(key))
    },
    
    clearAllCache: () => {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key)
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
    }
}

function DocumentSetsPage() {
    const { id } = useParams()
    const { isDemo, setIsDemo } = useDemo()
    const { roles } = useUser()

    // Check if user is a Member (read-only access)
    const isMemberRole = roles.includes('Member')

    const [step, setStep] = useState(0)
    const [documentType, setDocumentType] = useState('')

    const [documentName, setDocumentName] = useState('')
    const [productName, setProductName] = useState('')

    const [outline, setOutline] = useState('')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const [multipleFileList, setMultipleFileList] = useState([])
    const [multipleFileNameList, setMultipleFileNameList] = useState([])
    const [multipleFileContentList, setMultipleFileContentList] = useState([])

    // const [inputFiles, setInputFiles] = useState([])

    const [viewFileUrl, setViewFileUrl] = useState('')

    const [allDocuments, setAllDocuments] = useState([])
    const [allUsers, setAllUsers] = useState([])

    const [selectedFiles, setSelectedFiles] = useState([])
    const [existingMultipleFileNameList, setExistingMultipleFileNameList] =
        useState([])

    const [selectedDocumentToRename, setSelectedDocumentToRename] = useState(null)
    const [isRenameFileModalOpen, setIsRenameFileModalOpen] = useState(false)

    const [documentToDelete, setDocumentToDelete] = useState(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    const [documentToDuplicate, setDocumentToDuplicate] = useState(null)
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)

    // const [templateTypes, setTemplateTypes] = useState([])

    const [documentWarningModal, setDocumentWarningModal] = useState(false)
    const [addUsersModalOpen, setAddUsersModalOpen] = useState(false)
    // Menu states
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedDocument, setSelectedDocument] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    
    // Pagination states
    const [rowsPerPage, setRowsPerPage] = useState(10)

    // Generating Template Modal states
    const [status, setStatus] = useState('')
    const [generatingTemplateModalOpen, setGeneratingTemplateModalOpen] = useState(false)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [originalName, setOriginalName] = useState('')
    const hasProcessedSuccess = useRef(false)

    const authInfo = useAuthInfo()
    const orgs = authInfo.orgHelper.getOrgs()
    const orgId = orgs[0]?.orgId

    const navigate = useNavigate()
    const location = useLocation()

    const handleGeneratingTemplateModalClose = () => {
        setGeneratingTemplateModalOpen(false)
    }


    async function getOrgAccessToken() {
        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
        console.log('orgAccessToken', orgAccessToken)
        return orgAccessToken
    }

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const isFromSuccess = searchParams.get('success') === 'true'
        
        // Check for regeneration success message from sessionStorage
        const regenerationSuccess = sessionStorage.getItem('regenerationSuccess')
        if (regenerationSuccess && !hasProcessedSuccess.current) {
            try {
                const successData = JSON.parse(regenerationSuccess)
                // Only show if it's recent (within last 10 seconds)
                if (Date.now() - successData.timestamp < 10000) {
                    hasProcessedSuccess.current = true
                    setSuccessMessage(successData.message)
                    setShowSuccessAlert(true)
                    
                    // Auto-hide the alert after 6 seconds
                    setTimeout(() => {
                        setShowSuccessAlert(false)
                        hasProcessedSuccess.current = false
                    }, 6000)
                }
            } catch (error) {
                console.error('Error parsing regeneration success data:', error)
            } finally {
                // Clear the message from sessionStorage
                sessionStorage.removeItem('regenerationSuccess')
            }
        }
        
        if (isFromSuccess && !hasProcessedSuccess.current) {
            hasProcessedSuccess.current = true
            
            // Clear cache to ensure fresh data
            cacheHelpers.clearCache(orgId, id, 'document_sets')
            
            setSuccessMessage('Document successfully created!')
            setShowSuccessAlert(true)
            
            // Remove the success parameter from URL after showing alert
            navigate(`/documentSet/${id}`, { replace: true })
            
            // Auto-hide the alert after 6 seconds
            setTimeout(() => setShowSuccessAlert(false), 6000)
        } else if (!isFromSuccess && !regenerationSuccess) {
            // Reset the flag when navigating without success parameter or regeneration
            hasProcessedSuccess.current = false
        }
        
        async function retrieveOutline() {
            // setLoading(true) // Start loading
            try {
                const orgAccessToken = await getOrgAccessToken()
                const result = await getOutlineFromTemplates(
                    documentType.template_file_name,
                    documentType.nickname,
                    orgAccessToken
                ) // Assuming getDocumentSets returns the data
                setOutline(result) // Update state with fetched data
            } catch (error) {
                console.error(error)
                setError(true) // Set error state
            } finally {
                // setLoading(false) // End loading regardless of outcome
            }
        }

        if (documentType) {
            retrieveOutline()
        }
    }, [documentType])

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const isFromSuccess = searchParams.get('success') === 'true'
        
        async function retrieveData() {
            const orgAccessToken = await getOrgAccessToken()
            console.log('orgAccessToken', orgAccessToken.accessToken)
            console.log('orgAccessToken.accessToken', orgAccessToken.accessToken)
            
            // Check cache first (unless coming from success)
            if (!isFromSuccess) {
                const cachedData = cacheHelpers.getFromCache(orgId, id, 'document_sets')
                if (cachedData && cachedData['document_set'] && cachedData['document_set'].length > 0) {
                    console.log('[getFromCache] Using cached document sets data')

                    const documentSet = cachedData['document_set']
                    const users = cachedData['users']

                    console.log('[getFromCache] documentSet', documentSet)
                    console.log('[getFromCache] users', users)

                    setAllDocuments(documentSet ? documentSet : [])
                    setAllUsers(users ? users : [])
                    setError(false)
                    setLoading(false)
                    return
                }
            }
            
            setLoading(true)
            try {
                const result = await getOneDocumentSet(
                    setLoading,
                    setError,
                    id,
                    orgAccessToken
                )

                console.log('[retrieveData] result', result)

                const documentSet = result['document_set']
                const users = result['users']

                if (documentSet && documentSet.length > 0) {
                    setAllDocuments(documentSet)
                } else {
                    setAllDocuments([])
                }

                if (users) {
                    console.log('[retrieveData] users', users)
                    setAllUsers(users)
                } else {
                    setAllUsers([])
                }

                // Save to cache
                cacheHelpers.saveToCache(orgId, id, 'document_sets', result)
                setError(false)
            } catch (error) {
                console.error(error)
                setError(true)
            } finally {
                // Now we can safely set loading to false
                setLoading(false)
            }
        }

        // async function retrieveInputs() {
        //     try {
        //         const orgAccessToken = await getOrgAccessToken()
                
        //         // Check cache first
        //         const cachedData = cacheHelpers.getFromCache(orgId, 'input_files')
        //         if (cachedData) {
        //             console.log('Using cached input files data')
        //             setInputFiles(cachedData)
        //             return
        //         }
                
        //         const result = await listAllFiles('input', orgAccessToken)
        //         setInputFiles(result)
                
        //         // Save to cache
        //         cacheHelpers.saveToCache(orgId, 'input_files', result)
        //     } catch (error) {
        //         console.error(error)
        //         setError(true)
        //     }
        // }

        // async function retrieveTemplateTypes() {
        //     try {
        //         const orgAccessToken = await getOrgAccessToken()
                
        //         // Check cache first
        //         const cachedData = cacheHelpers.getFromCache(orgId, 'template_types')
        //         if (cachedData) {
        //             console.log('Using cached template types data')
        //             setTemplateTypes(cachedData)
        //             return
        //         }
                
        //         const result = await getTemplateTypes(orgAccessToken)
        //         setTemplateTypes(result)
                
        //         // Save to cache
        //         cacheHelpers.saveToCache(orgId, 'template_types', result)
        //     } catch (error) {
        //         console.error(error)
        //         setError(true)
        //     }
        // }

        // Execute all fetches
        Promise.all([
            retrieveData(),
            // retrieveInputs(),
            // retrieveTemplateTypes(),
        ]).catch((error) => {
            console.error('Error in data fetching:', error)
            setError(true)
            setLoading(false)
        })
    }, [location.search, id, orgId])

    const navigateToDocumentWithUrl = (params) => {
        navigate(`/document/${params.id}`)
    }

    const navigateToPageWithUrl = (params, id) => {
        navigate(`/${params}/${id}`)
    }

    // Menu handlers
    const handleMenuClick = (event, document) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
        setSelectedDocument(document)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        // setSelectedDocument(null)
    }

    const handleDeleteClick = () => {
        setDocumentToDelete(selectedDocument)
        setDeleteDialogOpen(true)
        handleMenuClose()
    }

    const handleDuplicateClick = () => {
        setDocumentToDuplicate(selectedDocument)
        setDuplicateDialogOpen(true)
        handleMenuClose()
    }

    // const handleShare = () => {
    //     console.log('Share document:', selectedDocument?.document_name)
    //     // Add share logic here
    // }

    // const handleSeeHistory = () => {
    //     console.log('See history for document:', selectedDocument?.document_name)
    //     // Add history logic here
    // }

    const updateSelectedDocumentToRename = (selected) => {
        const updatedSelected = { ...selectedDocumentToRename, ...selected }
        setSelectedDocumentToRename(updatedSelected)
    }

    const clearSelectedDocumentToRename = () => {
        setSelectedDocumentToRename(null)
        setSelectedDocument(null)
    }

    const handleSaveChanges = async () => {
        console.log('=== RENAME DOCUMENT SET: Save button clicked ===')
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        const jsonPayload = {
            document_id: selectedDocumentToRename.id,
            old_name: originalName,
            new_name: selectedDocumentToRename.document_name
        }
        console.log('JSON Payload being sent to backend:', JSON.stringify(jsonPayload, null, 2))
        const result = await updateDocumentName(orgAccessToken, jsonPayload)
        console.log('Backend response:', result)

        // Refresh document sets list from API and update cache
        console.log('Refreshing document sets from API...')
        const refreshedResult = await getOneDocumentSet(
            setLoading,
            setError,
            id,
            orgAccessToken
        )

        const documentSet = refreshedResult['document_set']
        const users = refreshedResult['users']

        // Update state with new data
        if (documentSet && documentSet.length > 0) {
            setAllDocuments(documentSet)
        } else {
            setAllDocuments([])
        }

        if (users) {
            setAllUsers(users)
        } else {
            setAllUsers([])
        }

        // Update cache with fresh data
        cacheHelpers.saveToCache(orgId, id, 'document_sets', refreshedResult)

        console.log('Document sets list refreshed and cache updated')
        console.log('=== RENAME DOCUMENT SET: Complete ===')
    }

    const hasChangesDocumentToRename = () => {
        return JSON.stringify(selectedDocumentToRename) !== JSON.stringify(selectedDocument)
    }

    const handleRenameClick = () => {
        console.log('handleRenameClick')
        console.log('selectedDocument', selectedDocument)
        console.log('Rename document:', selectedDocument?.document_name)
        // Store the original name before renaming
        setOriginalName(selectedDocument.document_name)
        setSelectedDocumentToRename(selectedDocument)
        setIsRenameFileModalOpen(true)
        console.log('selectedDocumentToRename', selectedDocumentToRename)
        handleMenuClose()
    }

    const handleRenameFileModalClose = () => {
        setIsRenameFileModalOpen(false)
    }

    const handleDeleteConfirm = async () => {
        // Placeholder delete function - just log/show message
        console.log('Delete document:', documentToDelete.document_name)
        console.log('documentToDelete', documentToDelete)
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
        const jsonPayload = {       
            document_id: documentToDelete.id
        }
        console.log('jsonPayload', jsonPayload)
        await deleteDocument(orgAccessToken, jsonPayload)
        
        // Clear cache after deletion
        cacheHelpers.clearCache(orgId, id, 'document_sets')
        
        navigate(`/documentSet/${id}`)
        // You could show a snackbar or alert here if needed
        setDeleteDialogOpen(false)
        setDocumentToDelete(null)
        setSelectedDocument(null)
        
        // Reload templates after deletion
        setStatus('loading')
        try {
            const result = await getOneDocumentSet(
                setLoading,
                setError,
                id,
                orgAccessToken
            )

            const documentSet = result['document_set']
            const users = result['users']

            setAllDocuments(documentSet)
            setAllUsers(users)
            
            // Save to cache
            cacheHelpers.saveToCache(orgId, id, 'document_sets', result)
            
            setStatus('success')
            
            // Show success message for deletion
            setSuccessMessage('Document deleted successfully!')
            setShowSuccessAlert(true)
            setTimeout(() => setShowSuccessAlert(false), 3000)
        } catch (error) {
            console.error(error)
            setStatus('error')
        } finally {
            setStatus('')
        }
    }

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false)
        setDocumentToDelete(null)
        setSelectedDocument(null)
    }

    const handleDuplicateConfirm = async () => {
        console.log('Duplicate document:', documentToDuplicate.document_name)
        const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)

        try {

            // Close dialog and set
            setDuplicateDialogOpen(false)
            setStatus('loading')
            setLoading(true)


            await duplicateDocument(documentToDuplicate.id, orgAccessToken)

            // Clear cache after duplication
            cacheHelpers.clearCache(orgId, id, 'document_sets')

            // Unselect 
            setDocumentToDuplicate(null)
            setSelectedDocument(null)

            // Reload documents after duplication
            const result = await getOneDocumentSet(
                setLoading,
                setError,
                id,
                orgAccessToken
            )

            const documentSet = result['document_set']
            const users = result['users']
            
            setAllDocuments(documentSet)
            setAllUsers(users)

            // Save to cache
            cacheHelpers.saveToCache(orgId, id, 'document_sets', result)

            setStatus('success')

            // Show success message for duplication
            setSuccessMessage('Document duplicated successfully!')
            setShowSuccessAlert(true)
            setTimeout(() => setShowSuccessAlert(false), 3000)
        } catch (error) {
            console.error('Error duplicating document:', error)
            setStatus('error')
        } finally {
            setStatus('')
            setLoading(false)
        }
    }

    const handleDuplicateCancel = () => {
        setDuplicateDialogOpen(false)
        setDocumentToDuplicate(null)
        setSelectedDocument(null)
    }

    const handleBackClick = () => {
        navigate('/')
    }

    const handleRefresh = async () => {
        // Clear cache for this workspace
        cacheHelpers.clearCache(orgId, id, 'document_sets')
        
        setLoading(true)
        setError(false)
        
        try {
            const orgAccessToken = await getOrgAccessToken()
            
            // Reload document sets
            const result = await getOneDocumentSet(
                setLoading,
                setError,
                id,
                orgAccessToken
            )

            console.log('[handleRefresh] Result of getOneDocumentSet', result)

            const documentSet = result['document_set']
            const users = result['users']
            
            console.log('[handleRefresh] Document Set', documentSet)
            console.log('[handleRefresh] Users', users)

            if (documentSet && documentSet.length > 0) {
                setAllDocuments(documentSet)
                setAllUsers(users)
                cacheHelpers.saveToCache(orgId, id, 'document_sets', result)
            } else {
                setAllDocuments([])
                setAllUsers([])
                cacheHelpers.saveToCache(orgId, id, 'document_sets', result)
            }
            
            setError(false)
        } catch (error) {
            console.error('Error refreshing data:', error)
            setError(true)
        } finally {
            setLoading(false)
        }
    }

    // const url = "https://biohaventeststorage.blob.core.windows.net/output/BHV%202100%20NCO%20Test%202%206-30.docx?sp=r&st=2024-06-30T23:07:34Z&se=2024-07-01T07:07:34Z&spr=https&sv=2022-11-02&sr=b&sig=GOnFNWnExYqZ4mbWRvwQKgvQfFXc9tZNIzrkBjdl2OE%3D"

    const resetAllFiles = () => {
        setMultipleFileList([])
        setMultipleFileNameList([])
        setMultipleFileContentList([])
    }

    const handleContinue = (increment = 1) => {
        setStep((prevStep) => prevStep + increment)
    }

    const handleReturn = () => {
        setStep((prevStep) => prevStep - 1)
    }

    const [newDocumentModal, setNewDocumentModal] = useState(false)

    const handleNewDocumentModalOpen = () => setNewDocumentModal(true)
    const handleNewDocumentModalClose = () => setNewDocumentModal(false)

    const handleClose = () => {
        handleNewDocumentModalClose()
        resetAllFiles()
        setDocumentType('')
        setDocumentName('')
        setProductName('')
        setOutline('')
        setStep(0)
    }

    // Status component with styling
    const StatusChip = ({ status }) => {
        let color, backgroundColor, label
        
        switch (status) {
            case 'Ready':
                color = '#059669'
                backgroundColor = '#D1FAE5'
                label = 'Draft Generated'
                break
            case 'Generating':
                color = '#D97706'
                backgroundColor = '#FEF3C7'
                label = 'Drafting In Progress'
                break
            case 'N/A':
            default:
                color = '#6B7280'
                backgroundColor = '#F3F4F6'
                label = 'Not started'
                break
        }
        
        return (
            <Chip
                label={label}
                size="small"
                sx={{
                    color,
                    backgroundColor,
                    fontWeight: 500,
                    fontSize: '12px',
                    height: '20px',
                    borderRadius: '10px',
                    '& .MuiChip-label': {
                        padding: '0 8px'
                    }
                }}
            />
        )
    }

    // Transform documents for new table structure
    const transformedData = allDocuments.map((document) => ({
        id: document.id,
        name: document.document_name,
        type: document.template_nickname,
        createdBy: document.user || 'Jane Doe',
        created: formatCreatedDate(document),
        status: document.status,
        document_name: document.document_name,
        document_set_name: document.document_set_name,
        url: document.url
    }))

    // Define table columns for new UI
    const tableColumns = [
        {
            field: 'name',
            header: 'Name',
            width: '50%',
        },
        // {
        //     field: 'type',
        //     header: 'Type',
        //     width: '20%',
        // },
        {
            field: 'createdBy',
            header: 'Created by',
            width: '15%',
        },
        {
            field: 'status',
            header: 'Status',
            width: '20%',
            render: (value) => <StatusChip status={value} />
        },
        {
            field: 'created',
            header: 'Created',
            width: '15%',
        },
    ]

    const handleSelectFile = (index, fileName, isRemove) => {
        if (isRemove) {
            // Remove the file from selectedFiles and existingMultipleFileNameList
            setSelectedFiles(selectedFiles.filter((i) => i !== index))
            setExistingMultipleFileNameList(
                existingMultipleFileNameList.filter((name) => name !== fileName)
            )
        } else {
            // Add the file to selectedFiles and existingMultipleFileNameList
            setSelectedFiles([...selectedFiles, index])
            setExistingMultipleFileNameList([
                ...existingMultipleFileNameList,
                fileName,
            ])
        }
    }

    const handleMultipleFiles = (files, fileContents) => {
        try {
            // Handle saving the file content or any other logic as needed for each file
            const newFiles = []
            console.log('files here', files)

            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                const content = fileContents[i]
                newFiles.push({ name: file.name, content })
                console.log('newFiles', newFiles)
            }

            // Update React state with the array of file objects
            setMultipleFileList(newFiles)
            setMultipleFileNameList(newFiles.map((file) => file.name))
            setMultipleFileContentList(fileContents)
        } catch (e) {
            console.log('Error in handling files', e)
        }
    }

    // Inside your target component
    const rowDetails = location.state

    // console.log(rowDetails); // This will log the row object passed as state

    const removeFile = (index) => {
        const newFileList = [...multipleFileList]
        newFileList.splice(index, 1)
        setMultipleFileList(newFileList) // Assuming your state setter is named like this
    }


    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage)
    }

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setCurrentPage(1)
    }

    const handleRowClick = (row) => {
        if (row.status === 'Ready') {
            navigateToDocumentWithUrl({ id: row.id })
        } else {
            console.log("roles", roles)
            if (roles.includes('Internal')) {
                console.log("roles", roles)
                navigateToDocumentWithUrl({ id: row.id })
            } else {
                console.log('row.status', row.status)
                setStatus('warning')
                setGeneratingTemplateModalOpen(true)
            }
        }
    }

    const handleRoleChange = async (userId, newRole) => {
        // Update local state optimistically with proper nested structure
        setAllUsers(prevUsers =>
            prevUsers.map(user => {
                if (user.user_id === userId) {
                    // Update both direct role property and nested structure
                    const updatedUser = { ...user, role: newRole }

                    // Also update nested structure if it exists
                    if (user.org_id_to_org_info && user.org_id_to_org_info[orgId]) {
                        updatedUser.org_id_to_org_info = {
                            ...user.org_id_to_org_info,
                            [orgId]: {
                                ...user.org_id_to_org_info[orgId],
                                user_role: newRole
                            }
                        }
                    }

                    return updatedUser
                }
                return user
            })
        )

        try {
            const orgAccessToken = await getOrgAccessToken()
            const jsonPayload = {
                user_id: userId,
                new_role: newRole
            }

            await updateRoles(jsonPayload, orgAccessToken)

            // Clear cache after role update
            cacheHelpers.clearCache(orgId, id, 'document_sets')

            // Show success message
            setSuccessMessage('User role updated successfully!')
            setShowSuccessAlert(true)
            setTimeout(() => setShowSuccessAlert(false), 3000)
        } catch (error) {
            console.error('Error updating role:', error)

            // Revert the optimistic update on error
            try {
                const result = await getOneDocumentSet(
                    setLoading,
                    setError,
                    id,
                    await getOrgAccessToken()
                )
                setAllUsers(result.users || [])
            } catch (revertError) {
                console.error('Error reverting role change:', revertError)
            }
        }
    }

    const handleInviteUser = async (email, role) => {
        try {
            const orgAccessToken = await getOrgAccessToken()

            // Clear cache and refresh users
            cacheHelpers.clearCache(orgId, id, 'document_sets')

            const result = await getOneDocumentSet(
                setLoading,
                setError,
                id,
                orgAccessToken
            )

            setAllUsers(result.users || [])

            // Show success message
            setSuccessMessage('User invited successfully!')
            setShowSuccessAlert(true)
            setTimeout(() => setShowSuccessAlert(false), 3000)
        } catch (error) {
            console.error('Error inviting user:', error)
        }
    }

    return (
        <Box sx={{
            width: '100%',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
            height: '100%',
            overflowY: 'auto',
        }}>
            <Box sx={{ 
                width: '100%', 
                height: '100%', 
                padding: '40px 64px', 
                boxSizing: 'border-box',
                gap: '24px',
                display: 'flex',
                flexDirection: 'column', 
                backgroundColor: '#F9FAFB',
            }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '8px 0px',
                        backgroundColor: '#f9fafb',
                        flexShrink: 0
                    }}
                >
                    {/* Back Arrow and Three Dots Menu Row */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: '8px'
                        }}
                    >
                        <IconButton
                            onClick={handleBackClick}
                            sx={{
                                padding: '6px',
                            marginLeft: '-8px',
                                marginTop: '-8px',
                                color: '#374151',
                                '&:hover': {
                                    backgroundColor: '#f3f4f6'
                                }
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: '20px', color: '#374151' }} />
                        </IconButton>
                    </Box>
                    <Box sx={{
                        width: '100%',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <Typography sx={{
                                fontSize: '24px',
                                lineHeight: '36px',
                                fontWeight: 700,
                                color: '#374151',
                            }}>
                                Workspace: {id}
                            </Typography>
                            <IconButton
                                onClick={handleRefresh}
                                disabled={loading}
                                title="Refresh documents"
                                size="small"
                                sx={{
                                    color: '#6B7280',
                                    '&:hover': {
                                        backgroundColor: '#F3F4F6',
                                    },
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<PeopleIcon />}
                                onClick={() => setAddUsersModalOpen(true)}
                                sx={{
                                    backgroundColor: '#ffffff',
                                    borderColor: '#d1d5db',
                                    color: '#374151',
                                    height: '34px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    padding: '0 16px',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '8px',
                                        color: '#6b7280',
                                        '& svg': {
                                            fontSize: '16px',
                                        },
                                    },
                                }}
                            >
                                Users · {allUsers.length}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => !isMemberRole && navigateToPageWithUrl('createDocument', id)}
                                disabled={isMemberRole}
                                sx={{
                                    backgroundColor: '#1F477D',
                                    // borderColor: '#FFFFFF',
                                    color: '#FFFFFF',
                                    height: '34px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    padding: '0 16px',
                                    '&:hover': {
                                        backgroundColor: '#183864',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#9CA3AF',
                                        color: '#E5E7EB',
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '8px',
                                        '& svg': {
                                            fontSize: '16px',
                                        },
                                    },
                                }}
                            >
                                Create document
                            </Button>
                        </Box>
                        {/* </Box> */}
                    </Box>
                </Box>
                
                {showSuccessAlert && (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 2,
                            maxWidth: 'fit-content',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                        }}
                        onClose={() => setShowSuccessAlert(false)}
                    >
                        {successMessage}
                    </Alert>
                )}
                
                {/* {!loading && error && <div>Error...</div>} */}
                
                {loading ? (
                    <CustomTableSkeleton
                        columns={tableColumns}
                        backgroundColor={'#F9FAFB'}
                    />
                ) : (
                    <Box sx={{ 
                        height: '100%'
                    }}>
                        <CustomTable
                            columns={tableColumns}
                            data={transformedData}
                            onRowClick={handleRowClick}
                            onMenuClick={handleMenuClick}
                            backgroundColor={'#F9FAFB'}
                        />
                    </Box>
                )}
                
                {/* Options Menu */}
                <OptionsMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    onDelete={handleDeleteClick}
                    onRename={handleRenameClick}
                    onDuplicate={handleDuplicateClick}
                />

                {/* Delete confirmation dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleDeleteCancel}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Delete Document</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete "{documentToDelete?.document_name}"? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Duplicate confirmation dialog */}
                <Dialog
                    open={duplicateDialogOpen}
                    onClose={handleDuplicateCancel}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Duplicate Document</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to duplicate "{documentToDuplicate?.document_name}"?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDuplicateCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDuplicateConfirm} color="primary" variant="contained">
                            Duplicate
                        </Button>
                    </DialogActions>
                </Dialog>

                <GeneratingTemplateModal
                    open={generatingTemplateModalOpen}
                    onClose={() => {
                        console.log("onClose Clicked")
                        handleGeneratingTemplateModalClose()
                    }}
                    loading={status === 'loading'}
                    success={status === 'success'}
                    successMessage={'Your document is currently being created.'}
                    error={status === 'error'}
                    errorMessage={''}
                    warning={status === 'warning'}
                    warningMessage={'Your document is still drafting.'}
                    onSuccess={() => {
                        navigate(`/documentSet/${id}?success=true`)
                    }}
                    onError={() => {
                        // Error handling is done in handleReviewConfirm
                    }}
                    // onWarning={() => {
                    //     console.log("onWarning Clicked")
                    //     // Warning handling is done in handleReviewConfirm
                    //     navigate(`/documentSet/${id}`)
                    //     setGeneratingTemplateModalOpen(false)
                    // }}
                />
                
                {/* Document Warning Modal */}
                <Modal
                    open={documentWarningModal}
                    onClose={() => setDocumentWarningModal(false)}
                >
                    <Box className="modal-background">
                        <Box className="modal-content">
                            <Box className="form-container">
                                <h2>Document Not Ready</h2>
                                <p>This document is not ready for viewing yet. Please wait for generation to complete.</p>
                                <button
                                    className="modal-submit"
                                    onClick={() => setDocumentWarningModal(false)}
                                >
                                    <p className="text-white">OK</p>
                                </button>
                            </Box>
                        </Box>
                    </Box>
                </Modal>

                <RenameFileModal
                    open={isRenameFileModalOpen}
                    onClose={handleRenameFileModalClose}
                    modalTitle="Document"
                    selected={selectedDocumentToRename}
                    value='document_name'
                    updateSelected={updateSelectedDocumentToRename}
                    saveChanges={handleSaveChanges}
                    clearSelected={clearSelectedDocumentToRename}
                    hasChanges={hasChangesDocumentToRename}
                />
                <UsersAndPermissionsModal
                    open={addUsersModalOpen}
                    onClose={() => setAddUsersModalOpen(false)}
                    users={allUsers}
                    onInvite={handleInviteUser}
                    onRoleChange={handleRoleChange}
                    orgId={orgId}
                    context="document_set"
                    contextId={id}
                    accessToken={authInfo.getAccessTokenSilently ? authInfo.getAccessTokenSilently() : null}
                />
            </Box>
        </Box>
    )
}

export default DocumentSetsPage
